import {
  computed,
  reactive,
  type ComponentPublicInstance,
  type ComputedRef,
  type Reactive,
  type Ref,
  type ShallowRef,
} from 'vue';
import { getGeoItemMeta } from 'src/lib/geoItemHelper';
import {
  showWaitSpinnerTabs,
  createNewTabs,
  prepareTabResources,
} from 'src/lib/tabPaneHelper';
import GIMovieManager from 'src/lib/geo_item/GIMovieManager';
import type { MastersContext } from 'src/models/master';
import type {
  GeoItemMeta,
  GeoItemsMeta,
  GIResource,
  MapSelectOpts,
  TryRefreshCurrentSelectedElemInfoParams,
} from 'src/models/geoItem';
import type {
  IExtremeMapRef,
  LayerInfo,
  Location,
  MapElemInfo,
  MovieLayerMeta,
  Tab,
} from 'src/models';
import type { IGeoItemSearchRef } from 'src/models/geoItemSearch';
import type { User } from 'src/models/user';
import { createInjection } from 'src/lib/createInjection';

// defineExpose({ getSearchParams }) により実行時は IGeoItemSearchRef を満たす
type GeoItemSearchInstance = ComponentPublicInstance & IGeoItemSearchRef<unknown>;

// provideGeoItemLayersが構築・provideするstoreの公開インターフェース
export interface GeoItemLayersStore {
  geoItemSearchRefMap: Reactive<Record<string, GeoItemSearchInstance>>;
  geoItemSearchRef: (name: string) => (el: ComponentPublicInstance | null) => void;
  visibleGeoItemLayers: ComputedRef<GeoItemMeta[]>;
  isMaxVisibleGeoItemLayersReached: ComputedRef<boolean>;
  initializeDataManagers: (
    origList: LayerInfo[],
    opts: { user: User; masters: MastersContext },
  ) => Promise<void>;
  showInitialDataLayers: () => void;
  changeGeoItemLayers: (dataName: string) => Promise<void>;
  refreshGeoItemLayers: (
    params: { spinnerMsg: string },
  ) => Promise<Array<{ item: GeoItemMeta; resources: GIResource[] }> | undefined>;
  onMapZoomChanged: (params: { zoom: number | undefined; oldZoom: number | undefined }) => void;
}

const { provide: provideGeoItemLayersCtx, inject: injectGeoItemLayersCtx } =
  createInjection<GeoItemLayersStore>('useGeoItemLayers');

export function useGeoItemLayersStore(): GeoItemLayersStore {
  return injectGeoItemLayersCtx();
}

// provide/injectは親→子方向のみで、provider自身はこのstoreをinjectできないため、
// provider側でも利用できるようprovideするだけでなくstoreを返り値でも渡す
export function provideGeoItemLayers(deps: {
  geoItemsMeta: GeoItemsMeta;
  movieLayerMeta: ShallowRef<MovieLayerMeta>;
  refExtremeMap: Ref<IExtremeMapRef | undefined>;
  tabs: Ref<Tab[]>;
  waitSpinner: { show: boolean; msg: string; msgDefault: string };
  tryRefreshCurrentSelectedElemInfo: (p: TryRefreshCurrentSelectedElemInfoParams) => void;
  setMapSelectedElemInfo: (val: MapElemInfo<GIResource | Location> | null, opts?: MapSelectOpts) => void;
  getSelectedElemDataName: () => string | null;
}): GeoItemLayersStore {
  const { geoItemsMeta, movieLayerMeta, refExtremeMap, tabs, waitSpinner } = deps;

  // v-for 内の function ref で各 GeoItemSearch コンポーネントを名前をキーにして格納する
  const geoItemSearchRefMap = reactive<Record<string, GeoItemSearchInstance>>({});
  const geoItemSearchRef = (name: string) => (el: ComponentPublicInstance | null) => {
    if (el) geoItemSearchRefMap[name] = el as GeoItemSearchInstance;
    else delete geoItemSearchRefMap[name];
  };

  const visibleGeoItemLayers = computed(() => {
    return Object.entries(geoItemsMeta.show).filter(ent => !!ent[1]).map(ent => {
      return geoItemsMeta.map[ent[0]];
    });
  });
  const isMaxVisibleGeoItemLayersReached = computed(() => {
    return visibleGeoItemLayers.value.length >= 4;
  });

  const initializeDataManagers = async(
    origList: LayerInfo[],
    { user, masters }: { user: User; masters: MastersContext },
  ) => {
    const map = await getGeoItemMeta(origList, { useGIManager: true, user, masters });
    geoItemsMeta.list = Object.values(map);
    geoItemsMeta.map = map;

    // movie関連は別途保存しておく
    try {
      const giManager = new GIMovieManager({ masters });
      const item = {
        name: 'movie',
        dispName: '動画',
        manager: giManager,
        layerManager: null,
      };
      movieLayerMeta.value = item;
    } catch (e) {
      // movieレイヤーが使えなくても他レイヤーの初期化は継続する
      console.error('initializeDataManagers. failed to create GIMovieManager', e);
    }
  };
  const showInitialDataLayers = () => {
    // 車と、チェックがついてるやつ
    const initialLayers = ['car'];
    for (const [dataName, isShown] of Object.entries(geoItemsMeta.show)) {
      if (isShown) { initialLayers.push(dataName); }
    }
    for (const dataName of initialLayers) {
      if (!refExtremeMap.value) continue;
      const metaItem = geoItemsMeta.map[dataName];
      const zIndexOffset = geoItemsMeta.order[dataName] || 0;
      refExtremeMap.value.showDataLayer(metaItem, [], zIndexOffset);
    }
  };
  const changeGeoItemLayers = async(dataName: string) => {
    if (geoItemsMeta.show[dataName]) {
      geoItemsMeta.order[dataName] = visibleGeoItemLayers.value.length;
    } else {
      const deletedOrder = geoItemsMeta.order[dataName];
      delete geoItemsMeta.order[dataName];
      // 順番の付け替え
      for (const [k, v] of Object.entries(geoItemsMeta.order)) {
        if (v > deletedOrder) {
          geoItemsMeta.order[k]--;
        }
      }
      delete geoItemsMeta.countsDisp[dataName];
    }
    // 編集中にレイヤーを消されたら追随して消える
    const selectedDataName = deps.getSelectedElemDataName();
    if (selectedDataName) {
      // carとmovieは関係ないので除外
      const ignoreDataNames = ['car', 'movie'];
      if (
        !ignoreDataNames.includes(selectedDataName) &&
        !geoItemsMeta.show[selectedDataName]
      ) {
        deps.setMapSelectedElemInfo(null);
      }
    }
    // 再取得、再表示
    await refreshGeoItemLayers({ spinnerMsg: waitSpinner.msgDefault });
  };
  const refreshGeoItemLayers = async({ spinnerMsg }: { spinnerMsg: string }) => {
    const reqItems: GeoItemMeta[] = [];
    for (const [dataName, show] of Object.entries(geoItemsMeta.show)) {
      const metaItem = geoItemsMeta.map[dataName];
      if (!show && refExtremeMap.value) {
        refExtremeMap.value.removeDataLayer(metaItem.name);
        continue;
      }
      // あとでまとめてリクエスト
      reqItems.push(metaItem);
    }

    const currentTabs = tabs.value.filter(e => {
      return [...reqItems.map(e => e.name), 'comment'].includes(e.dataName);
    });
    if (reqItems.length === 0) {
      tabs.value = currentTabs;
      return;
    }

    waitSpinner.msg = spinnerMsg;
    waitSpinner.show = true;
    showWaitSpinnerTabs(currentTabs);
    const newTabs = createNewTabs(tabs.value, reqItems);
    tabs.value = [...currentTabs, ...newTabs];
    const results = await getGeoItems(reqItems);
    waitSpinner.show = false;

    for (const { item: metaItem, resources } of results) {
      const dataName = metaItem.name;
      const zIndexOffset = geoItemsMeta.order[dataName] || 0;
      if (refExtremeMap.value) {
        refExtremeMap.value.showDataLayer(metaItem, resources, zIndexOffset);
      }
      deps.tryRefreshCurrentSelectedElemInfo({ dataName, resources });
      geoItemsMeta.countsDisp[dataName] = `(${resources.length}件)`;
      prepareTabResources(tabs.value, metaItem, resources);
    }

    return results;
  };
  const getGeoItems = async(reqItems: GeoItemMeta[]) => {
    const promises = reqItems.flatMap(item => {
      const componentRef = geoItemSearchRefMap[item.name];
      if (!componentRef) return [];

      return [(async() => {
        const resources = await item.manager.getResources(componentRef);
        return { item, resources };
      })()];
    });
    return Promise.all(promises);
  };
  const onMapZoomChanged = ({ zoom, oldZoom }: { zoom: number | undefined; oldZoom: number | undefined }) => {
    visibleGeoItemLayers.value.forEach(layerInfo => {
      layerInfo.layerManager.refreshLayerOnZoomChange({ zoom, oldZoom });
    });
  };

  const store = {
    geoItemSearchRefMap,
    geoItemSearchRef,
    visibleGeoItemLayers,
    isMaxVisibleGeoItemLayersReached,
    initializeDataManagers,
    showInitialDataLayers,
    changeGeoItemLayers,
    refreshGeoItemLayers,
    onMapZoomChanged,
  };
  provideGeoItemLayersCtx(store);
  return store;
}
